import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  FlatList,
  Image,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import CloseIcon from '../../../assets/icons/close-icon.svg';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import supabase from '../../utils/supabaseClient';

const BREAKPOINT_WIDTH = 768;

/**
 * Custom dropdown selector for make/model selection.
 */
const Dropdown = ({ label, placeholder, options, selectedValue, onSelect, disabled, style, inline, scrollRef }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchText) return options;
    const lower = searchText.toLowerCase();
    return options.filter(item => item.toLowerCase().includes(lower));
  }, [options, searchText]);

  const handleSelect = useCallback((value) => {
    onSelect(value);
    setIsOpen(false);
    setSearchText('');
  }, [onSelect]);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    setIsOpen(prev => {
      if (prev) {
        setSearchText('');
      } else if (scrollRef?.current) {
        setTimeout(() => scrollRef.current?.scrollToEnd?.({ animated: true }), 100);
      }
      return !prev;
    });
  }, [disabled, scrollRef]);

  return (
    <View style={[styles.dropdownContainer, style]}>
      <Text style={styles.dropdownLabel}>{label}</Text>
      <TouchableOpacity
        style={[styles.dropdownTrigger, disabled && styles.dropdownDisabled]}
        onPress={handleToggle}
        disabled={disabled}
      >
        <Text style={[styles.dropdownText, !selectedValue && styles.dropdownPlaceholder]}>
          {selectedValue || placeholder}
        </Text>
        <Text style={styles.dropdownArrow}>{isOpen ? '\u25B2' : '\u25BC'}</Text>
      </TouchableOpacity>
      {isOpen && (
        <View style={[styles.dropdownList, inline && styles.dropdownListInline]}>
          <TextInput
            style={styles.dropdownSearch}
            placeholder="Type to filter..."
            placeholderTextColor="rgba(255, 255, 255, 0.3)"
            value={searchText}
            onChangeText={setSearchText}
          />
          <FlatList
            data={filteredOptions}
            keyExtractor={(item) => item}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={[styles.dropdownItem, item === selectedValue && styles.dropdownItemSelected]}
                onPress={() => handleSelect(item)}
              >
                <Text style={[styles.dropdownItemText, item === selectedValue && styles.dropdownItemTextSelected]}>
                  {item}
                </Text>
              </TouchableOpacity>
            )}
            style={styles.dropdownFlatList}
            keyboardShouldPersistTaps="handled"
          />
        </View>
      )}
    </View>
  );
};

/**
 * Square card for a recommended model (not yet selected for feed).
 */
const QuickPickCard = ({ model, onSelect, isSelecting }) => (
  <TouchableOpacity
    style={styles.quickPickCard}
    onPress={() => onSelect(model)}
    disabled={isSelecting}
    activeOpacity={0.7}
  >
    {model.primaryImageUrl ? (
      <Image
        source={{ uri: model.primaryImageUrl }}
        style={styles.quickPickImage}
        resizeMode="cover"
      />
    ) : (
      <View style={[styles.quickPickImage, styles.quickPickImagePlaceholder]}>
        <Icon name="car" size={32} color="rgba(255,255,255,0.3)" />
      </View>
    )}
    <View style={styles.quickPickOverlay}>
      <View style={styles.quickPickScoreBadge}>
        <Text style={styles.quickPickScoreText}>{model.scoreValue}%</Text>
      </View>
    </View>
    <View style={styles.quickPickInfo}>
      <Text style={styles.quickPickMake} numberOfLines={1}>{model.make}</Text>
      <Text style={styles.quickPickModel} numberOfLines={1}>{model.model}</Text>
    </View>
    <View style={styles.quickPickCheck}>
      <Icon name="plus-circle-outline" size={20} color="rgba(255,255,255,0.6)" />
    </View>
  </TouchableOpacity>
);

/**
 * Modal for adding a car to recommendations.
 * Shows recommended quick-pick cards for not-yet-selected models,
 * plus make/model dropdowns for manual search.
 */
const AddCarModal = ({ visible, onClose, onSuccess, recommendedModels = [], onQuickSelect, userAddedCount = 0, addedVia = null }) => {
  const { session, region, onboardingIntent } = useAuth();
  const MAX_USER_ADDED_CARS = onboardingIntent === 'deals' ? 3 : 1;
  const posthog = usePostHog();
  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);
  const [makesModels, setMakesModels] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [selectedMake, setSelectedMake] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isQuickSelecting, setIsQuickSelecting] = useState(false);
  const [pendingQuickPick, setPendingQuickPick] = useState(null); // model awaiting confirmation
  const [errorMessage, setErrorMessage] = useState(null);
  const modalScrollRef = useRef(null);

  const isMobile = screenWidth < BREAKPOINT_WIDTH;

  // Track screen width for responsive layout
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenWidth(window.width);
    });
    return () => subscription?.remove();
  }, []);

  // Track modal open/close
  useEffect(() => {
    if (visible) {
      posthog?.capture('add_car_modal_opened');
    }
  }, [visible, posthog]);

  // Fetch makes/models when modal opens
  useEffect(() => {
    if (!visible) return;

    const fetchMakesModels = async () => {
      setIsLoadingData(true);
      try {
        const { data, error } = await supabase
          .from('car_makes_models')
          .select('make_name, model_name')
          .eq('region', region || 'UK')
          .order('make_name')
          .order('model_name');

        if (error) throw error;
        setMakesModels(data || []);
      } catch (error) {
        logger.error('[AddCarModal] Error fetching makes/models:', error);
        setErrorMessage('Failed to load car data. Please try again.');
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchMakesModels();
  }, [visible, region]);

  // Reset state when modal closes
  const wasVisibleRef = useRef(false);
  useEffect(() => {
    if (visible) {
      wasVisibleRef.current = true;
    } else if (wasVisibleRef.current) {
      wasVisibleRef.current = false;
      posthog?.capture('add_car_modal_closed');
      setSelectedMake(null);
      setSelectedModel(null);
      setErrorMessage(null);
      setIsSubmitting(false);
      setIsQuickSelecting(false);
      setPendingQuickPick(null);
    }
  }, [visible, posthog]);

  // Compute distinct makes
  const makes = useMemo(() => {
    const uniqueMakes = [...new Set(makesModels.map(item => item.make_name))];
    return uniqueMakes.sort();
  }, [makesModels]);

  // Compute models for selected make
  const models = useMemo(() => {
    if (!selectedMake) return [];
    return makesModels
      .filter(item => item.make_name === selectedMake)
      .map(item => item.model_name)
      .sort();
  }, [makesModels, selectedMake]);

  const handleMakeSelect = useCallback((make) => {
    setSelectedMake(make);
    setSelectedModel(null);
    setErrorMessage(null);
    posthog?.capture('add_car_make_selected', { make });
  }, [posthog]);

  const handleModelSelect = useCallback((model) => {
    setSelectedModel(model);
    setErrorMessage(null);
    posthog?.capture('add_car_model_selected', { make: selectedMake, model });
  }, [posthog, selectedMake]);

  const handleQuickPickTap = useCallback((model) => {
    setPendingQuickPick(model);
  }, []);

  const handleQuickPickConfirm = useCallback(async () => {
    if (!onQuickSelect || isQuickSelecting || !pendingQuickPick) return;

    setIsQuickSelecting(true);
    posthog?.capture('quick_pick_confirmed', {
      model_id: pendingQuickPick.modelId,
      make: pendingQuickPick.make,
      model: pendingQuickPick.model,
      score_value: pendingQuickPick.scoreValue,
    });

    try {
      await onQuickSelect(pendingQuickPick.modelId);
      onClose();
    } catch (error) {
      logger.error('[AddCarModal] Quick select error:', error);
      setErrorMessage('Failed to add car. Please try again.');
      setPendingQuickPick(null);
    } finally {
      setIsQuickSelecting(false);
    }
  }, [onQuickSelect, isQuickSelecting, pendingQuickPick, posthog, onClose]);

  const handleQuickPickCancel = useCallback(() => {
    setPendingQuickPick(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedMake || !selectedModel || !session?.user?.id) return;

    setIsSubmitting(true);
    setErrorMessage(null);
    posthog?.capture('add_car_submitted', { make: selectedMake, model: selectedModel, added_via: addedVia });

    try {
      const { error } = await supabase
        .from('user_car_requests')
        .insert({
          user_id: session.user.id,
          make: selectedMake,
          model: selectedModel,
          added_via: addedVia,
        });

      if (error) {
        // Trigger RAISE EXCEPTION messages come through as Postgres errors
        const message = error.message || 'Something went wrong. Please try again.';

        // Map trigger error messages to user-friendly text and an analytics-friendly kind
        let userMessage = message;
        let errorKind = 'unknown';
        if (message.includes('only available for UK')) {
          userMessage = 'Car search is currently only available for UK users.';
          errorKind = 'region_locked';
        } else if (message.includes('active car search')) {
          userMessage = 'You already have a car search in progress. Please wait for it to complete.';
          errorKind = 'in_progress';
        } else if (message.includes('maximum number')) {
          userMessage = 'You\'ve already added a car. Remove it first to search for a new one.';
          errorKind = 'capacity';
        } else if (message.includes('already in your recommendations')) {
          userMessage = 'This car is already in your recommendations.';
          errorKind = 'already_exists';
        } else if (message.includes('wait')) {
          userMessage = 'Please wait a few minutes before searching again.';
          errorKind = 'cooldown';
        } else if (message.includes('Invalid make/model')) {
          userMessage = 'Invalid car selection. Please try again.';
          errorKind = 'invalid_selection';
        }

        setErrorMessage(userMessage);
        posthog?.capture('add_car_failed', {
          make: selectedMake,
          model: selectedModel,
          error: message,
          error_kind: errorKind,
        });
        return;
      }

      // Success — close modal, parent shows banner
      onSuccess?.(selectedMake, selectedModel);
      onClose();
    } catch (error) {
      logger.error('[AddCarModal] Submit error:', error);
      setErrorMessage('Something went wrong. Please try again.');
      posthog?.capture('add_car_failed', {
        make: selectedMake,
        model: selectedModel,
        error: error.message,
        error_kind: 'exception',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedMake, selectedModel, session, posthog, onSuccess, onClose]);

  const hasQuickPicks = recommendedModels.length > 0 && !!onQuickSelect;

  return (
    <Modal animationType="fade" transparent={true} visible={visible} onRequestClose={onClose}>
      <View style={styles.centeredView}>
        <View style={[styles.modalView, isMobile && styles.modalViewMobile]}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <CloseIcon width="16" height="16" />
          </TouchableOpacity>

          <ScrollView
            ref={modalScrollRef}
            style={styles.scrollContainer}
            contentContainerStyle={[styles.content, isMobile && styles.contentMobile]}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <Text style={[styles.title, isMobile && styles.titleMobile]}>
              Search for a Car
            </Text>
            <Text style={styles.subtitle}>
              Choose a make and model to add to your recommendations.
              We'll score it against your preferences.
            </Text>

            {/* Quick-pick recommended models */}
            {hasQuickPicks && !pendingQuickPick && (
              <View style={styles.quickPickSection}>
                <Text style={styles.quickPickSectionTitle}>Recommended for You</Text>
                <View style={styles.quickPickGrid}>
                  {recommendedModels.map((model) => (
                    <QuickPickCard
                      key={model.modelId}
                      model={model}
                      onSelect={handleQuickPickTap}
                      isSelecting={isQuickSelecting}
                    />
                  ))}
                </View>
              </View>
            )}

            {/* Quick-pick confirmation */}
            {pendingQuickPick && (
              <View style={styles.confirmSection}>
                <Text style={styles.confirmTitle}>
                  Add {pendingQuickPick.make} {pendingQuickPick.model}?
                </Text>
                <Text style={styles.confirmSubtitle}>
                  This will add it to your tracked cars and start scanning for listings.
                </Text>
                {errorMessage && (
                  <Text style={styles.errorText}>{errorMessage}</Text>
                )}
                <View style={styles.confirmButtons}>
                  <TouchableOpacity
                    style={styles.confirmButtonCancel}
                    onPress={handleQuickPickCancel}
                    disabled={isQuickSelecting}
                  >
                    <Text style={styles.confirmButtonCancelText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.confirmButtonAdd, isQuickSelecting && styles.submitButtonDisabled]}
                    onPress={handleQuickPickConfirm}
                    disabled={isQuickSelecting}
                  >
                    {isQuickSelecting ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Text style={styles.confirmButtonAddText}>Add Car</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {/* Divider between quick picks and manual search */}
            {hasQuickPicks && !pendingQuickPick && (
              <View style={styles.sectionDivider}>
                <View style={styles.sectionDividerLine} />
                <Text style={styles.sectionDividerText}>or search manually</Text>
                <View style={styles.sectionDividerLine} />
              </View>
            )}

            {userAddedCount >= MAX_USER_ADDED_CARS ? (
              <Text style={styles.limitNote}>
                You've reached the maximum number of personal car searches.{'\n'}
                Remove your current search to add a new one.
              </Text>
            ) : isLoadingData ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#0556F7" />
                <Text style={styles.loadingText}>Loading cars...</Text>
              </View>
            ) : (
              <>
                <Dropdown
                  label="Make"
                  placeholder="Select a make..."
                  options={makes}
                  selectedValue={selectedMake}
                  onSelect={handleMakeSelect}
                  inline
                  scrollRef={modalScrollRef}
                />

                <Dropdown
                  label="Model"
                  placeholder={selectedMake ? 'Select a model...' : 'Select a make first'}
                  options={models}
                  selectedValue={selectedModel}
                  onSelect={handleModelSelect}
                  disabled={!selectedMake}
                  inline
                  scrollRef={modalScrollRef}
                />

                {errorMessage && (
                  <Text style={styles.errorText}>{errorMessage}</Text>
                )}

                <TouchableOpacity
                  style={[
                    styles.submitButton,
                    (!selectedMake || !selectedModel || isSubmitting) && styles.submitButtonDisabled,
                  ]}
                  onPress={handleSubmit}
                  disabled={!selectedMake || !selectedModel || isSubmitting}
                >
                  {isSubmitting ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text style={styles.submitButtonText}>Add Car</Text>
                  )}
                </TouchableOpacity>
              </>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalView: {
    width: 480,
    maxHeight: '85%',
    backgroundColor: '#13192A',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
  },
  modalViewMobile: {
    width: '90%',
    maxWidth: 400,
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  scrollContainer: {
    flexGrow: 0,
  },
  content: {
    padding: 40,
    paddingTop: 48,
  },
  contentMobile: {
    padding: 24,
    paddingTop: 48,
  },
  title: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 24,
    lineHeight: 32,
    color: '#FFFFFF',
    marginBottom: 8,
  },
  titleMobile: {
    fontSize: 20,
    lineHeight: 28,
  },
  subtitle: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    lineHeight: 22,
    color: 'rgba(255, 255, 255, 0.55)',
    marginBottom: 24,
  },
  // Quick-pick section
  quickPickSection: {
    marginBottom: 16,
  },
  quickPickSectionTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    lineHeight: 20,
    color: 'rgba(255, 255, 255, 0.4)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
  },
  quickPickGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  quickPickCard: {
    width: 185,
    height: 110,
    borderRadius: 12,
    backgroundColor: '#1D242F',
    overflow: 'hidden',
    position: 'relative',
  },
  quickPickImage: {
    width: '100%',
    height: '100%',
    position: 'absolute',
  },
  quickPickImagePlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1D242F',
  },
  quickPickOverlay: {
    position: 'absolute',
    top: 8,
    left: 8,
  },
  quickPickScoreBadge: {
    backgroundColor: 'rgba(20, 76, 184, 0.85)',
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  quickPickScoreText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 11,
    color: '#FFFFFF',
  },
  quickPickInfo: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 8,
    paddingTop: 20,
    backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
  },
  quickPickMake: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 10,
    color: 'rgba(255, 255, 255, 0.7)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  quickPickModel: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: '#FFFFFF',
    lineHeight: 16,
  },
  quickPickCheck: {
    position: 'absolute',
    top: 8,
    right: 8,
  },
  // Confirmation
  confirmSection: {
    marginBottom: 16,
    alignItems: 'center',
    paddingVertical: 8,
  },
  confirmTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 18,
    lineHeight: 26,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 8,
  },
  confirmSubtitle: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    lineHeight: 22,
    color: 'rgba(255, 255, 255, 0.55)',
    textAlign: 'center',
    marginBottom: 20,
  },
  confirmButtons: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  confirmButtonCancel: {
    flex: 1,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 32,
    paddingVertical: 12,
    alignItems: 'center',
  },
  confirmButtonCancelText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 15,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  confirmButtonAdd: {
    flex: 1,
    backgroundColor: '#0556F7',
    borderRadius: 32,
    paddingVertical: 12,
    alignItems: 'center',
  },
  confirmButtonAddText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 15,
    color: '#FFFFFF',
  },
  // Divider
  sectionDivider: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
  },
  sectionDividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  sectionDividerText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.35)',
  },
  // Loading
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.5)',
    marginTop: 12,
  },
  // Dropdowns
  dropdownContainer: {
    marginBottom: 16,
    zIndex: 1,
  },
  dropdownLabel: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    lineHeight: 20,
    color: 'rgba(255, 255, 255, 0.7)',
    marginBottom: 6,
  },
  dropdownTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
  },
  dropdownDisabled: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    opacity: 0.6,
  },
  dropdownText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 16,
    lineHeight: 24,
    color: '#FFFFFF',
  },
  dropdownPlaceholder: {
    color: 'rgba(255, 255, 255, 0.35)',
  },
  dropdownArrow: {
    fontSize: 10,
    color: 'rgba(255, 255, 255, 0.35)',
  },
  dropdownList: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#1D242F',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    marginTop: 4,
    zIndex: 100,
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.5)',
  },
  dropdownListInline: {
    position: 'relative',
    top: 0,
    left: 0,
    right: 0,
  },
  dropdownSearch: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    lineHeight: 20,
    color: '#FFFFFF',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.08)',
    outlineStyle: 'none',
  },
  dropdownFlatList: {
    maxHeight: 300,
  },
  dropdownItem: {
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  dropdownItemSelected: {
    backgroundColor: 'rgba(5, 86, 247, 0.15)',
  },
  dropdownItemText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 15,
    lineHeight: 22,
    color: 'rgba(255, 255, 255, 0.85)',
  },
  dropdownItemTextSelected: {
    fontFamily: 'Satoshi-Medium',
    color: '#5B8DEF',
  },
  limitNote: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    lineHeight: 22,
    color: 'rgba(255, 255, 255, 0.45)',
    textAlign: 'center',
    paddingVertical: 16,
  },
  errorText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 13,
    lineHeight: 20,
    color: '#D32F2F',
    marginBottom: 12,
  },
  submitButton: {
    backgroundColor: '#0556F7',
    borderRadius: 32,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitButtonText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 16,
    color: '#FFFFFF',
  },
});

export default AddCarModal;
